import React, { useEffect, useState } from "react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, ChevronDown, ChevronUp } from "lucide-react";

const STATUSES = ["ny", "bekreftet", "pågår", "ferdig", "avvist"];
const STATUS_STYLE = {
  ny: "bg-warm/20 text-navy", bekreftet: "bg-electric/10 text-electric",
  "pågår": "bg-blue-100 text-blue-700", ferdig: "bg-green-100 text-green-700", avvist: "bg-red-100 text-red-700",
};

export default function Bookings() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/admin/bookings").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    const r = await api.put(`/admin/bookings/${id}/status`, { status });
    const sent = r.data?.email_sent;
    setRows((rs) => rs.map((b) => (b.id === id ? { ...b, status, confirmation_sent_at: sent ? new Date().toISOString() : b.confirmation_sent_at } : b)));
    if (status === "bekreftet") {
      if (sent) toast.success("Bekreftet – e-post sendt til kunden");
      else toast.warning("Bekreftet, men e-post til kunden ble ikke sendt");
    } else toast.success("Status oppdatert");
  };
  const remove = async (id) => {
    if (!window.confirm("Slette denne bestillingen?")) return;
    await api.delete(`/admin/bookings/${id}`);
    setRows((rs) => rs.filter((r) => r.id !== id));
    toast.success("Slettet");
  };

  return (
    <div data-testid="admin-bookings">
      <h1 className="font-heading text-2xl font-bold text-navy">Bestillinger</h1>
      <p className="mt-1 text-sm text-ink/60">{rows.length} totalt</p>
      <div className="mt-6 space-y-3">
        {rows.length === 0 && <p className="text-ink/50 text-sm">Ingen bestillinger ennå.</p>}
        {rows.map((r) => (
          <div key={r.id} className="rounded-xl border border-hairline bg-white shadow-sm" data-testid={`booking-row-${r.id}`}>
            <div className="flex items-center justify-between gap-3 p-4 cursor-pointer" onClick={() => setOpen(open === r.id ? null : r.id)}>
              <div className="min-w-0">
                <p className="font-semibold text-navy truncate">{r.name} · {r.device_type} {r.model}</p>
                <p className="text-sm text-ink/60 truncate">{r.problem} · {r.preferred_date && r.preferred_time ? <span className="font-medium text-navy" data-testid="booking-row-slot">{r.preferred_date.split("-").reverse().join(".")} kl. {r.preferred_time}</span> : "Ingen tid valgt"} · mottatt {new Date(r.created_at).toLocaleString("nb-NO")}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLE[r.status] || "bg-slate-100"}`}>{r.status}</span>
                {open === r.id ? <ChevronUp className="h-4 w-4 text-ink/40" /> : <ChevronDown className="h-4 w-4 text-ink/40" />}
              </div>
            </div>
            {open === r.id && (
              <div className="border-t border-hairline p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {[["Telefon", r.phone], ["E-post", r.email], ["Merke", r.brand || "–"], ["Ønsket tid", r.preferred_date && r.preferred_time ? `${r.preferred_date.split("-").reverse().join(".")} kl. ${r.preferred_time}` : "–"], ["Estimert pris", r.price_estimate || "–"], ["Beskrivelse", r.description || "–"], ["Bekreftelse sendt", r.confirmation_sent_at ? new Date(r.confirmation_sent_at).toLocaleString("nb-NO") : "Nei"]].map(([k, v]) => (
                  <div key={k}><span className="text-ink/50">{k}: </span><span className="font-medium text-navy">{v}</span></div>
                ))}
                {r.image_id && <div className="sm:col-span-2"><img src={fileUrl(r.image_id)} alt="Enhet" className="h-28 rounded-lg border border-hairline object-cover" /></div>}
                <div className="sm:col-span-2 mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-ink/50">Status:</span>
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(r.id, s)} className={`rounded-full px-3 py-1 text-xs font-medium border ${r.status === s ? "border-electric bg-electric text-white" : "border-hairline text-navy hover:border-electric"}`} data-testid={`booking-status-${s}`}>{s}</button>
                  ))}
                  <button onClick={() => remove(r.id)} className="ml-auto inline-flex items-center gap-1 text-xs text-red-600 hover:underline" data-testid="booking-delete"><Trash2 className="h-3.5 w-3.5" /> Slett</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
